/**
 * ActionValidator — NLHE action rules per /design/specs/5_game_engine_spec.md §7.
 *
 * Returns the set of legal actions for the current to-act player given a
 * HandState, and validates incoming actions for legality + structural sanity.
 *
 * V1 is heads-up only. No side pots (both players are always covered for the
 * same all-in commitment — see game engine spec §1.1).
 */

import type { ActionKind, PlayerAction } from '@/types/action';
import type { HandState } from '@/types/handState';

// =============================================================================
// Legal action enumeration
// =============================================================================

export interface ActionOption {
  kind: ActionKind;
  /** For bet / raise: minimum total this-street investment. */
  minTotal?: number;
  /** For bet / raise: maximum total this-street investment (= stack + already invested). */
  maxTotal?: number;
  /** For call: chips owed (and committed if the player calls). */
  callAmount?: number;
  /** For allin: chips committed (= actor's remaining stack). */
  allinAmount?: number;
}

/**
 * Return the set of legal actions for the current to-act player. Empty array
 * if the phase is not street_open or no one is to act (terminated / closed).
 */
export function legalActions(state: HandState): ActionOption[] {
  if (state.phase !== 'street_open' || !state.toAct) return [];

  const actor = state.toAct;
  const stack = state.stacks[actor];
  const investedThisStreet = state.investedThisStreet[actor];
  const toCall = state.toCall;
  const bb = state.blinds.bb;

  if (stack === 0) return []; // already all-in; advance instead

  const options: ActionOption[] = [];

  if (toCall === 0) {
    // No bet to face — check / bet / allin
    options.push({ kind: 'check' });

    // Bet legal if stack > 0; min bet = 1 BB; max = full stack
    if (stack >= bb) {
      options.push({
        kind: 'bet',
        minTotal: investedThisStreet + bb,
        maxTotal: investedThisStreet + stack,
      });
    }
    options.push({ kind: 'allin', allinAmount: stack });
    return options;
  }

  // Bet to face
  options.push({ kind: 'fold' });

  // Call: if stack ≥ toCall, normal call; if stack < toCall, this collapses
  // to all-in-for-stack (handled below). We still expose 'call' for the case
  // where stack ≥ toCall.
  if (stack >= toCall) {
    options.push({ kind: 'call', callAmount: toCall });
  }

  // Raise: only when betting is reopened AND we can afford min raise
  if (state.bettingReopened) {
    const currentBetLevel = investedThisStreet + toCall;
    const minRaiseDelta = Math.max(state.lastRaiseIncrement, bb);
    const minRaiseTotal = currentBetLevel + minRaiseDelta;
    const maxRaiseTotal = investedThisStreet + stack;
    // Raise legal only if we can reach minRaiseTotal (otherwise → all-in
    // doesn't reopen, exposed via 'allin' option instead).
    if (maxRaiseTotal >= minRaiseTotal) {
      options.push({
        kind: 'raise',
        minTotal: minRaiseTotal,
        maxTotal: maxRaiseTotal,
      });
    }
  }

  // All-in: always available if stack > 0
  options.push({ kind: 'allin', allinAmount: stack });

  return options;
}

// =============================================================================
// Action validation
// =============================================================================

export interface ValidationError {
  reason: string;
}

/**
 * Validate that `action` is legal given `state`. Returns null if legal, or
 * a structured error explaining why not.
 */
export function validateAction(
  state: HandState,
  action: PlayerAction,
): ValidationError | null {
  if (state.phase !== 'street_open' || !state.toAct) {
    return { reason: `cannot act in phase=${state.phase}` };
  }

  const actor = state.toAct;
  const stack = state.stacks[actor];
  const investedThisStreet = state.investedThisStreet[actor];
  const toCall = state.toCall;
  const bb = state.blinds.bb;

  switch (action.kind) {
    case 'fold': {
      if (toCall === 0) return { reason: 'cannot fold when nothing to call' };
      return null;
    }

    case 'check': {
      if (toCall !== 0) return { reason: 'cannot check when facing a bet' };
      return null;
    }

    case 'call': {
      if (toCall === 0) return { reason: 'nothing to call' };
      if (stack < toCall) {
        return { reason: 'insufficient stack to call (use allin)' };
      }
      return null;
    }

    case 'bet': {
      if (toCall !== 0) return { reason: 'cannot bet when facing a bet (raise instead)' };
      const minBetTotal = investedThisStreet + bb;
      const maxBetTotal = investedThisStreet + stack;
      if (action.total < minBetTotal) {
        return { reason: `bet below minimum (need ≥${minBetTotal})` };
      }
      if (action.total > maxBetTotal) {
        return { reason: `bet above stack (max ${maxBetTotal})` };
      }
      return null;
    }

    case 'raise': {
      if (toCall === 0) return { reason: 'cannot raise without a bet to face' };
      if (!state.bettingReopened) {
        return { reason: 'betting closed (previous all-in below min-raise)' };
      }
      const currentBetLevel = investedThisStreet + toCall;
      const minRaiseDelta = Math.max(state.lastRaiseIncrement, bb);
      const minRaiseTotal = currentBetLevel + minRaiseDelta;
      const maxRaiseTotal = investedThisStreet + stack;
      if (action.total < minRaiseTotal) {
        return { reason: `raise below minimum (need ≥${minRaiseTotal})` };
      }
      if (action.total > maxRaiseTotal) {
        return { reason: `raise above stack (max ${maxRaiseTotal})` };
      }
      return null;
    }

    case 'allin': {
      if (stack === 0) return { reason: 'no chips to all-in' };
      return null;
    }
  }
}

/** True if `action` is legal. Convenience wrapper. */
export function isLegalAction(state: HandState, action: PlayerAction): boolean {
  return validateAction(state, action) === null;
}
